/**
 * 文件大小单位转换器
 * 将字节数转换为可读的文件大小格式
 */

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @param decimals 保留小数位数，默认2位
 * @param binary 是否使用二进制计算（1024），默认true；false则使用十进制（1000）
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSize(bytes: number, decimals: number = 2, binary: boolean = true): string {
    if (bytes === 0) return '0 B';

    const k = binary ? 1024 : 1000;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = binary
        ? ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * 格式化文件大小（中文单位）
 * @param bytes 字节数
 * @param decimals 保留小数位数，默认2位
 * @returns 格式化后的文件大小字符串（中文单位）
 */
export function formatFileSizeCN(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 字节';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['字节', 'KB', 'MB', 'GB', 'TB', 'PB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    if (i === 0) {
        return bytes + ' ' + sizes[i];
    }

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * 智能格式化文件大小
 * 根据大小自动选择最合适的单位和小数位数
 * @param bytes 字节数
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSizeSmart(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // 根据大小自动调整小数位数
    let decimals: number;
    if (i === 0) {
        decimals = 0; // 字节不需要小数
    } else if (i === 1) {
        decimals = 1; // KB显示1位小数
    } else {
        decimals = 2; // 其他单位显示2位小数
    }

    const value = bytes / Math.pow(k, i);

    // 如果结果是整数，不显示小数点
    if (value % 1 === 0) {
        return value + ' ' + sizes[i];
    }

    return parseFloat(value.toFixed(decimals)) + ' ' + sizes[i];
}

/**
 * 解析文件大小字符串为字节数
 * @param sizeStr 文件大小字符串，如 "1.5 MB"
 * @returns 字节数
 */
export function parseFileSize(sizeStr: string): number {
    const regex = /^(\d+(?:\.\d+)?)\s*([KMGTPEZY]?B)$/i;
    const match = sizeStr.trim().match(regex);

    if (!match) {
        throw new Error('Invalid file size format');
    }

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    const multipliers: { [key: string]: number } = {
        'B': 1,
        'KB': 1024,
        'MB': 1024 ** 2,
        'GB': 1024 ** 3,
        'TB': 1024 ** 4,
        'PB': 1024 ** 5,
        'EB': 1024 ** 6,
        'ZB': 1024 ** 7,
        'YB': 1024 ** 8,
    };

    return value * (multipliers[unit] || 1);
}

/**
 * 比较两个文件大小
 * @param size1 第一个文件大小（字节或字符串）
 * @param size2 第二个文件大小（字节或字符串）
 * @returns -1: size1 < size2, 0: size1 = size2, 1: size1 > size2
 */
export function compareFileSize(size1: number | string, size2: number | string): number {
    const bytes1 = typeof size1 === 'string' ? parseFileSize(size1) : size1;
    const bytes2 = typeof size2 === 'string' ? parseFileSize(size2) : size2;

    if (bytes1 < bytes2) return -1;
    if (bytes1 > bytes2) return 1;
    return 0;
}

/**
 * 文件大小范围检查
 * @param bytes 文件大小（字节）
 * @param minSize 最小大小（字节）
 * @param maxSize 最大大小（字节）
 * @returns 是否在范围内
 */
export function isFileSizeInRange(bytes: number, minSize: number, maxSize: number): boolean {
    return bytes >= minSize && bytes <= maxSize;
}

/**
 * 常用文件大小常量
 */
export const FILE_SIZE_CONSTANTS = {
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
    TB: 1024 ** 4,
    // 常用限制
    MAX_AVATAR_SIZE: 5 * 1024 ** 2, // 5MB
    MAX_IMAGE_SIZE: 10 * 1024 ** 2, // 10MB
    MAX_FILE_SIZE: 100 * 1024 ** 2, // 100MB
    MAX_VIDEO_SIZE: 500 * 1024 ** 2, // 500MB
} as const;
